<template>
    <div class="segments" v-loading.fullscreen.lock="fullscreenLoading">
        <div class="page-header">
            <h1 class="page-title">
                Сегменты
                <span class="title-note"> - Доступна загрузка только хешированных данных</span>
            </h1>
            <button class="create-button" @click="showAddModal = true">
                <img src="/images/plus.svg" alt="add">
                Добавить сегмент
            </button>
        </div>

        <div class="content">
            <div class="search-container">
                <div class="search-input-wrapper">
                    <img src="/images/search.svg" alt="search" class="search-icon" />
                    <input type="text" class="search-input" v-model="searchQuery" placeholder="Search" />
                </div>
            </div>

            <table class="segments-table table">
                <thead>
                    <tr>
                        <th class="name">Наименование</th>
                        <th class="type">Тип</th>
                        <th class="bundle">Bundle ID</th>
                        <th class="data-rows">Строки</th>
                        <th class="audience">Аудитория</th>
                        <th class="meta">Аудитория Meta</th>
                        <th class="upload-date">Дата загрузки</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="segment in filteredSegments" :key="segment.id">
                        <td class="name">
                            <div class="name-content">
                                <span class="title">{{ segment.name }}</span>
                                <span
                                    v-if="segmentStatusLabel(segment)"
                                    class="segment-status-pill"
                                    :class="segmentStatusClass(segment)"
                                    :title="segmentStatusTitle(segment) || undefined"
                                >{{ segmentStatusLabel(segment) }}</span>
                                <div v-if="segment.description" class="description-text">
                                    {{ segment.description }}
                                </div>
                            </div>
                        </td>
                        <td class="type">
                            {{ getTypeLabel(segment.type) }}
                        </td>
                        <td class="bundle">
                            <template v-if="segment.bundle">
                                <a v-if="isUrl(segment.bundle)" :href="segment.bundle" target="_blank"
                                    class="bundle-link">
                                    <img :src="getBundleIcon(segment.bundle)" :alt="getBundleType(segment.bundle)"
                                        class="bundle-icon" />
                                </a>
                                <img v-else :src="getBundleIcon(segment.bundle)" :alt="getBundleType(segment.bundle)"
                                    class="bundle-icon" />
                            </template>
                            <span v-else>—</span>
                        </td>
                        <td class="data-rows">
                            {{ segment.data_rows ? formatNumber(segment.data_rows) : '—' }}
                        </td>
                        <td class="audience">
                            <div class="audience-number">
                                {{ segment.audience_size ? formatNumber(segment.audience_size) : '—' }}
                            </div>
                            <div class="audience-matching">
                                <small v-if="segment.audience_size && segment.data_rows">
                                    {{ computeMatchingPercentage(segment.audience_size, segment.data_rows) }}%
                                </small>
                                <small v-else>—</small>
                            </div>
                        </td>
                        <td class="meta">
                            <div class="meta-number">
                                {{ segment.meta_audience_size ? formatNumber(segment.meta_audience_size) : '—' }}
                            </div>
                            <div class="meta-matching">
                                <small v-if="segment.meta_audience_size && segment.data_rows">
                                    {{ computeMatchingPercentage(segment.meta_audience_size, segment.data_rows)
                                    }}%
                                </small>
                                <small v-else>—</small>
                            </div>
                        </td>
                        <td class="upload-date">
                            {{ segment.created_at ? formatDate(segment.created_at) : '—' }}
                        </td>
                    </tr>
                    <tr v-if="filteredSegments.length === 0">
                        <td colspan="7" class="empty-state">
                            {{ searchQuery ? 'Сегменты не найдены' : 'Нет сегментов' }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Модальное окно добавления сегмента -->
        <div v-if="showAddModal" class="modal-overlay" @click="closeModal">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>Добавить сегмент</h3>
                    <button class="btn-close" @click="closeModal">
                        <img src="/images/close.svg" alt="Закрыть" />
                    </button>
                </div>

                <div class="modal-body">
                    <div class="form-group">
                        <label class="form-label">Наименование сегмента *</label>
                        <input type="text" class="form-input" v-model="newSegment.name"
                            placeholder="Введите наименование сегмента" />
                        <div v-if="validationErrors.name" class="error-message">{{ validationErrors.name }}</div>
                    </div>

                    <div class="form-group">
                        <label class="form-label">Описание сегмента</label>
                        <textarea class="form-textarea" v-model="newSegment.description" rows="3"
                            placeholder="Введите описание сегмента"></textarea>
                    </div>

                    <div class="form-group">
                        <label class="form-label">Выберите тип *</label>
                        <div class="radio-group">
                            <label class="radio-label">
                                <input type="radio" value="phones" v-model="newSegment.type" />
                                <span>Контактная информация (телефоны)</span>
                            </label>
                            <label class="radio-label">
                                <input type="radio" value="email" v-model="newSegment.type" />
                                <span>Контактная информация (email)</span>
                            </label>
                            <label class="radio-label">
                                <input type="radio" value="mobile_device_id" v-model="newSegment.type" />
                                <span>Mobile Device ID</span>
                            </label>
                        </div>
                        <div v-if="validationErrors.type" class="error-message">{{ validationErrors.type }}</div>
                    </div>

                    <div class="form-group bundle-group" v-if="newSegment.type === 'mobile_device_id'">
                        <label class="form-label">Bundle ID</label>
                        <input type="text" class="form-input" v-model="newSegment.bundle"
                            placeholder="Укажите Bundle ID или ссылку на приложение (https://play.google.com/store/apps/... или https://apps.apple.com/...)" />
                    </div>

                    <div class="form-group">
                        <label class="form-label">Загрузить данные *</label>
                        <div class="upload-options">
                            <label class="radio-label">
                                <input type="radio" value="file" v-model="uploadMethod" />
                                <span>Из файла</span>
                            </label>
                        </div>

                        <div class="file-upload">
                            <input type="file" ref="fileInput" accept=".csv,.txt" @change="handleFileSelect"
                                class="file-input" />
                            <div v-if="selectedFile" class="file-info">
                                <span>{{ selectedFile.name }}</span>
                                <div v-if="fileRowCountSkipped" class="file-row-count file-row-count-muted">
                                    Крупный файл — строки посчитает сервер после загрузки.
                                </div>
                                <div v-else-if="fileRowCount > 0" class="file-row-count">
                                    Строк: {{ fileRowCount.toLocaleString() }}
                                </div>
                                <button type="button" class="btn-remove-file" @click="removeFile">
                                    <img src="/images/close.svg" alt="Удалить" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal-footer">
                    <button class="btn-secondary" @click="closeModal">Отмена</button>
                    <button class="btn-primary" @click="createSegment" :disabled="isCreating || !canSubmit">
                        {{ isCreating ? 'Загрузка...' : 'Загрузить' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";

export default {
    name: "Segments",
    props: ["segments", "user"],
    data() {
        return {
            fullscreenLoading: false,
            searchQuery: "",
            showAddModal: false,
            isCreating: false,
            uploadMethod: "file",
            selectedFile: null,
            fileRowCount: 0,
            fileRowCountSkipped: false,
            localSegments: [],
            pollTimers: {},
            pollAttempts: {},
            pollFailureNotified: {},
            validationErrors: {},
            newSegment: {
                name: "",
                description: "",
                type: "",
                bundle: "",
                dataRows: "",
            },
        };
    },
    created() {
        this.localSegments = Array.isArray(this.segments) ? [...this.segments] : [];
    },
    beforeUnmount() {
        Object.keys(this.pollTimers).forEach((id) => this.stopPollingSegment(Number(id)));
    },
    watch: {
        segments: {
            handler(next) {
                this.localSegments = Array.isArray(next) ? [...next] : [];
            },
            deep: true,
        },
    },
    computed: {
        filteredSegments() {
            if (!this.searchQuery) {
                return this.localSegments;
            }

            const query = this.searchQuery.toLowerCase();
            return this.localSegments.filter(segment => {
                return (
                    segment.name?.toLowerCase().includes(query) ||
                    segment.description?.toLowerCase().includes(query) ||
                    this.getTypeLabel(segment.type).toLowerCase().includes(query)
                );
            });
        },
        canSubmit() {
            return !!this.selectedFile;
        },
    },
    methods: {
        getTypeLabel(type) {
            const labels = {
                phones: "Контактная информация (телефоны)",
                email: "Контактная информация (email)",
                mobile_device_id: "Mobile Device ID",
            };
            return labels[type] || type;
        },
        formatNumber(number) {
            return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        },
        formatDate(dateString) {
            if (!dateString) return '—';
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}.${month}.${year}`;
        },
        computeMatchingPercentage(audienceSize, fileRowsCount) {
            if (!fileRowsCount || fileRowsCount === 0 || !audienceSize) return '0.00';
            const percent = (Number(audienceSize) / Number(fileRowsCount)) * 100;
            return Number.isFinite(percent) ? percent.toFixed(2) : '0.00';
        },
        getBundleType(bundle) {
            if (!bundle) return null;
            const bundleLower = bundle.toLowerCase();
            // Определяем по URL
            if (bundleLower.includes('apps.apple.com') || bundleLower.includes('itunes.apple.com')) {
                return 'ios';
            }
            if (bundleLower.includes('play.google.com')) {
                return 'android';
            }
            // Если это не URL, определяем по содержимому
            if (bundleLower.includes('ios') || bundleLower.includes('apple')) {
                return 'ios';
            }
            // По умолчанию считаем Android
            return 'android';
        },
        isUrl(str) {
            if (!str) return false;
            try {
                const url = new URL(str);
                return url.protocol === 'http:' || url.protocol === 'https:';
            } catch {
                return false;
            }
        },
        getBundleIcon(bundle) {
            const type = this.getBundleType(bundle);
            return type === 'ios' ? '/images/ios.svg' : '/images/android.svg';
        },
        closeModal() {
            this.showAddModal = false;
            this.resetForm();
        },
        resetForm() {
            this.newSegment = {
                name: "",
                description: "",
                type: "",
                bundle: "",
                dataRows: "",
            };
            this.uploadMethod = "file";
            this.selectedFile = null;
            this.fileRowCount = 0;
            this.fileRowCountSkipped = false;
            this.validationErrors = {};
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = "";
            }
        },
        async handleFileSelect(event) {
            const file = event.target.files[0];
            if (file) {
                // (Frontend: no extension or size checks — validation is handled server-side)

                try {
                    const maxFullReadBytes = 3 * 1024 * 1024;
                    if (file.size > maxFullReadBytes) {
                        this.fileRowCount = 0;
                        this.fileRowCountSkipped = true;
                        this.selectedFile = file;
                        return;
                    }
                    this.fileRowCountSkipped = false;
                    this.fileRowCount = await this.countFileLines(file);
                    this.selectedFile = file;
                } catch (error) {
                    this.$notify({
                        title: "Ошибка",
                        message: "Ошибка обработки файла: " + error.message,
                        type: "error",
                    });
                }
            }
        },
        removeFile() {
            this.selectedFile = null;
            this.fileRowCount = 0;
            this.fileRowCountSkipped = false;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = "";
            }
        },
        // (Removed header validation: frontend no longer enforces specific first-line headers)
        async countFileLines(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const text = e.target.result;
                    const lines = text.split('\n');
                    // Учитываем, что последняя строка может быть пустой
                    const actualLines = lines.filter(line => line.trim().length > 0);
                    resolve(actualLines.length);
                };
                reader.onerror = () => reject(new Error('Ошибка подсчета строк'));
                reader.readAsText(file, 'UTF-8');
            });
        },
        getRowCount() {
            if (!this.newSegment.dataRows) return 0;
            const rows = this.newSegment.dataRows.split("\n").filter(row => row.trim());
            return rows.length;
        },
        validateForm() {
            this.validationErrors = {};

            if (!this.newSegment.name?.trim()) {
                this.validationErrors.name = "Наименование сегмента обязательно";
            }

            if (!this.newSegment.type) {
                this.validationErrors.type = "Выберите тип сегмента";
            }

            if (!this.selectedFile) {
                this.validationErrors.file = "Выберите файл для загрузки";
            }

            return Object.keys(this.validationErrors).length === 0;
        },
        async createSegment() {
            if (!this.validateForm()) return;

            this.isCreating = true;
            this.fullscreenLoading = true;

            try {
                // Отправляем единый multipart-запрос с файлом и метаданными
                const formData = new FormData();
                formData.append("file", this.selectedFile);
                formData.append("name", this.newSegment.name);
                formData.append("description", this.newSegment.description || "");
                formData.append("type", this.newSegment.type);
                if (this.newSegment.type === "mobile_device_id" && this.newSegment.bundle) {
                    formData.append("bundle", this.newSegment.bundle);
                }

                const response = await axios.post("/segments", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });

                if (response.data.success) {
                    const seg = response.data.segment;
                    if (seg && seg.id) {
                        this.localSegments.unshift({ ...seg });
                        this.startPollingSegment(seg.id);
                    }

                    this.closeModal();

                    this.$notify({
                        title: "Успех",
                        message: response.data.message || "Сегмент принят в обработку",
                        type: "success",
                    });
                } else {
                    throw new Error(response.data.message || "Ошибка при создании сегмента");
                }
            } catch (error) {
                if (error.response?.data?.errors) {
                    const errors = error.response.data.errors;
                    this.validationErrors = {};
                    Object.keys(errors).forEach(key => {
                        this.validationErrors[key] = errors[key][0];
                    });
                } else {
                    this.$notify({
                        title: "Ошибка",
                        message: error.response?.data?.message || error.message || "Ошибка при создании сегмента",
                        type: "error",
                    });
                }

                // При ошибке очистим выбранный файл и связанные поля, чтобы пользователь загрузил заново.
                this.selectedFile = null;
                this.fileRowCount = 0;
                this.fileRowCountSkipped = false;
                if (this.$refs.fileInput) {
                    this.$refs.fileInput.value = "";
                }
            } finally {
                this.isCreating = false;
                this.fullscreenLoading = false;
            }
        },

        segmentStatusLabel(segment) {
            const st = segment.processing_status;
            if (st === "failed") {
                return "Ошибка";
            }
            if (st === "queued" || st === "processing") {
                return "Обработка…";
            }
            if (!st && !segment.file_url) {
                return "Обработка…";
            }
            return "";
        },

        segmentStatusClass(segment) {
            const st = segment.processing_status;
            if (st === "failed") {
                return "is-failed";
            }
            if (st === "queued" || st === "processing" || (!st && !segment.file_url)) {
                return "is-processing";
            }
            return "";
        },

        segmentStatusTitle(segment) {
            if (segment.processing_status === "failed" && segment.processing_error) {
                return segment.processing_error;
            }
            return "";
        },

        async refreshSegmentFromServer(id) {
            const { data } = await axios.get(`/segments/${id}/status`);
            const patch = data.segment;
            const idx = this.localSegments.findIndex((s) => Number(s.id) === Number(id));
            if (idx >= 0 && patch) {
                const merged = { ...this.localSegments[idx], ...patch };
                this.localSegments.splice(idx, 1, merged);
            }
        },

        stopPollingSegment(id) {
            if (this.pollTimers[id]) {
                clearInterval(this.pollTimers[id]);
                delete this.pollTimers[id];
            }
            delete this.pollAttempts[id];
        },

        startPollingSegment(id) {
            const maxAttempts = 180;

            const tick = async () => {
                try {
                    this.pollAttempts[id] = (this.pollAttempts[id] || 0) + 1;
                    if (this.pollAttempts[id] > maxAttempts) {
                        this.stopPollingSegment(id);
                        this.$notify({
                            title: "Внимание",
                            message: "Обработка всё ещё идёт. Обновите страницу через несколько минут.",
                            type: "warning",
                        });
                        return;
                    }

                    await this.refreshSegmentFromServer(id);
                    const seg = this.localSegments.find((s) => Number(s.id) === Number(id));
                    const st = seg?.processing_status;

                    if (st === "failed") {
                        if (!this.pollFailureNotified[id]) {
                            this.pollFailureNotified[id] = true;
                            this.$notify({
                                title: "Ошибка",
                                message: seg?.processing_error || "Не удалось загрузить файл в облако",
                                type: "error",
                            });
                        }
                        this.stopPollingSegment(id);
                        return;
                    }

                    if (st === "completed" || (seg?.file_url && st !== "queued" && st !== "processing")) {
                        this.stopPollingSegment(id);
                    }
                } catch (e) {
                    console.error("poll segment status", e);
                }
            };

            tick();
            this.pollTimers[id] = setInterval(tick, 2000);
        },
    },
};
</script>

<style scoped>
.segment-status-pill {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 600;
    border-radius: 999px;
    vertical-align: middle;
}
.segment-status-pill.is-processing {
    background: #fff8e6;
    color: #946200;
    border: 1px solid #f5d67b;
}
.segment-status-pill.is-failed {
    background: #fdecea;
    color: #b42318;
    border: 1px solid #f5c2c0;
}
.file-row-count-muted {
    color: #666;
    font-size: 12px;
}
</style>